import { create } from 'zustand';
import { supabase } from '../services/supabase';
import { Word, Category, PronunciationFeedback } from '../types';
import { useQuery } from '@tanstack/react-query';

interface GameState {
    isPlaying: boolean;
    gameMode: string | null;
    difficulty: number;
    currentWord: Word | null;
    score: number;
    streak: number;
    gameEnded: boolean;
}

interface RecordingState {
    isRecording: boolean;
    recordedAudioBlob: Blob | null;
}

interface GameStore {
    gameState: GameState;
    recordingState: RecordingState;
    feedback: PronunciationFeedback | null;
    startGame: (gameMode: string, difficulty: number, word: Word) => void;
    endGame: () => void;
    setCurrentWord: (word: Word) => void;
    setScore: (score: number) => void;
    setStreak: (streak: number) => void;
    startRecording: () => Promise<void>;
    stopRecording: () => Promise<Blob | null>;
    processAudio: (audioBlob: Blob, word: string) => Promise<void>;
    playRecordedAudio: () => void;
    clearFeedback: () => void;
    reset: () => void;
}

const initialGameState: GameState = {
    isPlaying: false,
    gameMode: null,
    difficulty: 1,
    currentWord: null,
    score: 0,
    streak: 0,
    gameEnded: false,
};

const initialRecordingState: RecordingState = {
    isRecording: false,
    recordedAudioBlob: null,
};

let mediaRecorder: MediaRecorder | null = null;
let audioChunks: Blob[] = [];

export const useGameStore = create<GameStore>((set, get) => ({
    gameState: initialGameState,
    recordingState: initialRecordingState,
    feedback: null,

    startGame: (gameMode, difficulty, word) => {
        set({
            gameState: {
                ...initialGameState,
                isPlaying: true,
                gameMode,
                difficulty,
                currentWord: word,
            },
            feedback: null,
        });
    },

    endGame: () => {
        set(state => ({ gameState: { ...state.gameState, isPlaying: false, gameEnded: true } }));
    },

    setCurrentWord: (word) => {
        set(state => ({ gameState: { ...state.gameState, currentWord: word }, feedback: null, recordingState: initialRecordingState }));
    },

    setScore: (score) => {
        set(state => ({ gameState: { ...state.gameState, score: state.gameState.score + score } }));
    },

    setStreak: (streak) => {
        set(state => ({ gameState: { ...state.gameState, streak: streak } }));
    },

    startRecording: async () => {
        if (mediaRecorder && mediaRecorder.state === 'recording') return;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorder = new MediaRecorder(stream);
            audioChunks = [];

            mediaRecorder.ondataavailable = event => {
                audioChunks.push(event.data);
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
                set({ recordingState: { isRecording: false, recordedAudioBlob: audioBlob } });
            };

            mediaRecorder.start();
            set({ recordingState: { isRecording: true, recordedAudioBlob: null } });
        } catch (error) {
            console.error('Error starting recording:', error);
        }
    },

    stopRecording: async () => {
        return new Promise((resolve) => {
            if (mediaRecorder && mediaRecorder.state === 'recording') {
                mediaRecorder.onstop = () => {
                    const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
                    set({ recordingState: { isRecording: false, recordedAudioBlob: audioBlob } });
                    resolve(audioBlob);
                };
                mediaRecorder.stop();
            } else {
                resolve(null);
            }
        });
    },

    processAudio: async (audioBlob, word) => {
        try {
            const arrayBuffer = await audioBlob.arrayBuffer();
            const response = await fetch('http://localhost:8000/analyse/gemini', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ audio: arrayBuffer, word })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const feedbackData: PronunciationFeedback = await response.json();
            set({ feedback: feedbackData });

            // Update score and streak based on feedback
            get().setScore(feedbackData.overall_score);
            if (feedbackData.overall_score > 80) {
                get().setStreak(get().gameState.streak + 1);
            }

        } catch (error) {
            console.error('Error processing audio:', error);
            set({ feedback: { overall_score: 0, accuracy: 0, fluency: 0, completeness: 0, feedback_text: 'Could not analyze audio.', suggestions: [] } });
        }
    },

    playRecordedAudio: () => {
        const { recordedAudioBlob } = get().recordingState;
        if (recordedAudioBlob) {
            const audioUrl = URL.createObjectURL(recordedAudioBlob);
            const audio = new Audio(audioUrl);
            audio.play();
        }
    },

    clearFeedback: () => {
        set({ feedback: null });
    },

    reset: () => {
        set({ gameState: initialGameState, recordingState: initialRecordingState, feedback: null });
    },
}));

export const useGameData = () => {
    const { data: categories, error: categoriesError, isLoading: categoriesLoading } = useQuery<Category[], Error>({
        queryKey: ['categories'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('categories_with_counts')
                .select('*')
                .eq('is_active', true)
                .order('sort_order');
            if (error) throw error;
            return (data || []).map((item: any) => ({
              ...item,
              created_at: item.created_at || null,
              updated_at: item.updated_at || null
            })) as Category[];
        },
    });

    const fetchWords = async (category: string, difficulty: number) => {
        const { data, error } = await supabase
            .from('words')
            .select('*')
            .eq('category', category)
            .eq('difficulty_level', difficulty)
            .limit(20);
        if (error) throw error;
        return data || [];
    };

    return {
        categories,
        categoriesError,
        categoriesLoading,
        fetchWords,
    };
};